import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Layout from '../components/Layout';
import { termsAPI } from '../services/api';
import { useToast } from '../components/Toast';
import {
  Search, Plus, RefreshCw, X, FileText, CheckCircle2, Eye, RotateCcw, Trash2, Pencil,
} from 'lucide-react';

function formatDate(dt) {
  if (!dt) return '—';
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ── Create Terms Modal ───────────────────────────────────────────────────────
function CreateTermsModal({ forRole, existingVersions, onSave, onClose, loading }) {
  const [version, setVersion] = useState('');
  const [title, setTitle]     = useState(
    forRole === 'CUSTOMER' ? 'Customer Terms & Conditions' : 'Seller Terms & Conditions'
  );
  const [content, setContent] = useState('');
  const [touched, setTouched] = useState({ version: false, title: false, content: false });

  const versionTrimmed = version.trim();
  const isDuplicate = existingVersions.some(
    v => v.trim().toLowerCase() === versionTrimmed.toLowerCase()
  );

  const errors = {
    version: !versionTrimmed
      ? 'Version is required (e.g. "1.0")'
      : (isDuplicate ? `Version "${versionTrimmed}" already exists` : ''),
    title: title.trim() ? '' : 'Title is required',
    content: content.trim() ? '' : 'Content is required',
  };
  const isValid = !errors.version && !errors.title && !errors.content;

  const touchAll = () => setTouched({ version: true, title: true, content: true });

  const handleSubmit = (e) => {
    e.preventDefault();
    touchAll();
    if (isValid) {
      onSave({ version: versionTrimmed, title: title.trim(), content: content.trim() });
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 640 }}>
        <div className="modal-header">
          <span className="modal-title">Publish New Terms & Conditions</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: -10, marginBottom: 18 }}>
          Publishing a new version makes it active immediately for {forRole === 'CUSTOMER' ? 'customers' : 'sellers'} and deactivates the
          current active version. {forRole === 'CUSTOMER' ? 'Customers' : 'Sellers'} who already accepted a previous version keep their record.
        </p>

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="terms-version">Version <span style={{ color: 'var(--red)' }}>*</span></label>
            <input
              id="terms-version"
              value={version}
              onChange={e => { setVersion(e.target.value); setTouched(t => ({ ...t, version: true })); }}
              onBlur={() => setTouched(t => ({ ...t, version: true }))}
              placeholder="e.g. 1.0"
              autoFocus
              style={touched.version && errors.version ? { borderColor: 'var(--red)', boxShadow: '0 0 0 3px rgba(239,68,68,0.12)' } : {}}
            />
            {touched.version && errors.version && (
              <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 5 }}>⚠ {errors.version}</p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="terms-title">Title <span style={{ color: 'var(--red)' }}>*</span></label>
            <input
              id="terms-title"
              value={title}
              onChange={e => { setTitle(e.target.value); setTouched(t => ({ ...t, title: true })); }}
              onBlur={() => setTouched(t => ({ ...t, title: true }))}
              placeholder="e.g. Seller Terms & Conditions"
              style={touched.title && errors.title ? { borderColor: 'var(--red)', boxShadow: '0 0 0 3px rgba(239,68,68,0.12)' } : {}}
            />
            {touched.title && errors.title && (
              <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 5 }}>⚠ {errors.title}</p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="terms-content">Content <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea
              id="terms-content"
              value={content}
              onChange={e => { setContent(e.target.value); setTouched(t => ({ ...t, content: true })); }}
              onBlur={() => setTouched(t => ({ ...t, content: true }))}
              placeholder="Full terms & conditions text (plain text or markdown)…"
              rows={12}
              style={{
                width: '100%', resize: 'vertical', fontFamily: 'var(--font)', fontSize: 13.5, lineHeight: 1.6,
                ...(touched.content && errors.content ? { borderColor: 'var(--red)', boxShadow: '0 0 0 3px rgba(239,68,68,0.12)' } : {}),
              }}
            />
            {touched.content && errors.content && (
              <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 5 }}>⚠ {errors.content}</p>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Publishing…' : 'Publish & Activate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

CreateTermsModal.propTypes = {
  forRole: PropTypes.oneOf(['SELLER', 'CUSTOMER']).isRequired,
  existingVersions: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

// ── Edit Terms Modal ─────────────────────────────────────────────────────────
function EditTermsModal({ forRole, term, onSave, onClose, loading }) {
  const [title, setTitle]     = useState(term.title || '');
  const [content, setContent] = useState(term.content || '');
  const [touched, setTouched] = useState({ title: false, content: false });

  const errors = {
    title: title.trim() ? '' : 'Title is required',
    content: content.trim() ? '' : 'Content is required',
  };
  const isValid = !errors.title && !errors.content;

  const touchAll = () => setTouched({ title: true, content: true });

  const handleSubmit = (e) => {
    e.preventDefault();
    touchAll();
    if (isValid) {
      onSave({ title: title.trim(), content: content.trim() });
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 640 }}>
        <div className="modal-header">
          <span className="modal-title">
            Edit v{term.version} <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>· {forRole === 'CUSTOMER' ? 'Customer' : 'Seller'}</span>
          </span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: -10, marginBottom: 18 }}>
          This edits the wording of an existing version — the version number and active/inactive status are unchanged.
          {term.isActive ? ' This version is currently active, so changes apply immediately.' : ''}
        </p>

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="terms-edit-title">Title <span style={{ color: 'var(--red)' }}>*</span></label>
            <input
              id="terms-edit-title"
              value={title}
              onChange={e => { setTitle(e.target.value); setTouched(t => ({ ...t, title: true })); }}
              onBlur={() => setTouched(t => ({ ...t, title: true }))}
              autoFocus
              style={touched.title && errors.title ? { borderColor: 'var(--red)', boxShadow: '0 0 0 3px rgba(239,68,68,0.12)' } : {}}
            />
            {touched.title && errors.title && (
              <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 5 }}>⚠ {errors.title}</p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="terms-edit-content">Content <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea
              id="terms-edit-content"
              value={content}
              onChange={e => { setContent(e.target.value); setTouched(t => ({ ...t, content: true })); }}
              onBlur={() => setTouched(t => ({ ...t, content: true }))}
              rows={12}
              style={{
                width: '100%', resize: 'vertical', fontFamily: 'var(--font)', fontSize: 13.5, lineHeight: 1.6,
                ...(touched.content && errors.content ? { borderColor: 'var(--red)', boxShadow: '0 0 0 3px rgba(239,68,68,0.12)' } : {}),
              }}
            />
            {touched.content && errors.content && (
              <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 5 }}>⚠ {errors.content}</p>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

EditTermsModal.propTypes = {
  forRole: PropTypes.oneOf(['SELLER', 'CUSTOMER']).isRequired,
  term: PropTypes.shape({
    version: PropTypes.string,
    title: PropTypes.string,
    content: PropTypes.string,
    isActive: PropTypes.bool,
  }).isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

// ── View Terms Modal ─────────────────────────────────────────────────────────
function ViewTermsModal({ term, onClose }) {
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 640 }}>
        <div className="modal-header">
          <span className="modal-title">
            {term.title} <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>· v{term.version}</span>
          </span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <span className={`badge ${term.isActive ? 'active' : 'inactive'}`}>
            {term.isActive ? 'Active' : 'Inactive'}
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Last updated {formatDate(term.updatedAt)}
          </span>
        </div>
        <div
          style={{
            maxHeight: '50vh', overflowY: 'auto', padding: 16, border: '1px solid var(--border)',
            borderRadius: 10, background: 'var(--bg-surface)', fontSize: 13.5, lineHeight: 1.7,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}
        >
          {term.content}
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

ViewTermsModal.propTypes = {
  term: PropTypes.shape({
    title: PropTypes.string,
    version: PropTypes.string,
    isActive: PropTypes.bool,
    updatedAt: PropTypes.string,
    content: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

// ── Activate Confirm Modal ───────────────────────────────────────────────────
function ActivateModal({ forRole, term, onConfirm, onClose, loading }) {
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <span className="modal-title">Re-activate this version?</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          This will make <strong>v{term.version} — {term.title}</strong> the active Terms & Conditions
          for {forRole === 'CUSTOMER' ? 'customers' : 'sellers'}, and deactivate whichever version is currently active.
        </p>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn btn-primary" onClick={onConfirm} disabled={loading}>
            {loading ? 'Activating…' : 'Activate'}
          </button>
        </div>
      </div>
    </div>
  );
}

ActivateModal.propTypes = {
  forRole: PropTypes.oneOf(['SELLER', 'CUSTOMER']).isRequired,
  term: PropTypes.shape({ version: PropTypes.string, title: PropTypes.string }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

// ── Delete Confirm Modal ─────────────────────────────────────────────────────
function DeleteTermsModal({ forRole, term, onConfirm, onClose, loading }) {
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <span className="modal-title">Delete this version?</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          This will permanently delete <strong>v{term.version} — {term.title}</strong> for{' '}
          {forRole === 'CUSTOMER' ? 'customers' : 'sellers'}. This action cannot be undone.
        </p>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
          <button
            className="btn btn-primary"
            style={{ background: 'var(--red)', borderColor: 'var(--red)' }}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

DeleteTermsModal.propTypes = {
  forRole: PropTypes.oneOf(['SELLER', 'CUSTOMER']).isRequired,
  term: PropTypes.shape({ version: PropTypes.string, title: PropTypes.string }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

// ── Main Terms Page ──────────────────────────────────────────────────────────
export default function Terms() {
  const [forRole,       setForRole]       = useState('SELLER'); // 'SELLER' | 'CUSTOMER'
  const [terms,         setTerms]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState('');
  const [modalState,    setModalState]    = useState(null); // { mode: 'create' | 'view' | 'activate' | 'delete', term? }
  const [actionLoading, setActionLoading] = useState(false);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await termsAPI.getAll(forRole);
      setTerms(res.data || []);
    } catch {
      toast('Failed to load Terms & Conditions', 'error');
    } finally {
      setLoading(false);
    }
  }, [toast, forRole]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async ({ version, title, content }) => {
    setActionLoading(true);
    try {
      await termsAPI.create(version, title, content, forRole);
      toast(`Version ${version} published and activated`, 'success');
      setModalState(null);
      load();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to publish new version';
      toast(msg, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = async ({ title, content }) => {
    setActionLoading(true);
    try {
      await termsAPI.update(modalState.term.id, title, content);
      toast(`Version ${modalState.term.version} updated`, 'success');
      setModalState(null);
      load();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update version';
      toast(msg, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleActivate = async () => {
    setActionLoading(true);
    try {
      await termsAPI.activate(modalState.term.id);
      toast(`Version ${modalState.term.version} activated`, 'success');
      setModalState(null);
      load();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to activate version';
      toast(msg, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await termsAPI.delete(modalState.term.id);
      toast(`Version ${modalState.term.version} deleted`, 'success');
      setModalState(null);
      load();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to delete version';
      toast(msg, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const filtered = terms.filter(t => {
    const q = search.toLowerCase();
    if (!q) return true;
    return t.version?.toLowerCase().includes(q) || t.title?.toLowerCase().includes(q);
  });

  const activeTerm = terms.find(t => t.isActive);

  return (
    <Layout title="Terms & Conditions" subtitle="Publish and manage versioned T&C for sellers and customers">
      {/* Role tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 18, background: 'var(--bg-surface)', padding: 4, borderRadius: 10, width: 'fit-content' }}>
        {[['SELLER', 'Seller'], ['CUSTOMER', 'Customer']].map(([role, label]) => (
          <button
            key={role}
            type="button"
            className={`btn btn-sm ${forRole === role ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => { if (forRole !== role) { setForRole(role); setSearch(''); } }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Top bar */}
      <div className="cat-topbar">
        <div className="cat-topbar-row1">
          <div className="search-box cat-search">
            <Search size={14} className="search-icon" />
            <input placeholder="Search by version or title..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={13} /></button>
          <button className="btn btn-primary btn-sm" onClick={() => setModalState({ mode: 'create' })}>
            <Plus size={15} /> New Version
          </button>
        </div>
      </div>

      {/* Active version summary */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <span className="card-title">Currently Active</span>
          {activeTerm && <span className="badge active"><CheckCircle2 size={11} /> Active</span>}
        </div>
        {activeTerm ? (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
              {activeTerm.title} <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>· v{activeTerm.version}</span>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>
              Last updated {formatDate(activeTerm.updatedAt)}
            </div>
            <button
              className="btn btn-ghost btn-sm"
              style={{ marginTop: 12 }}
              onClick={() => setModalState({ mode: 'view', term: activeTerm })}
            >
              <Eye size={13} /> View content
            </button>
          </div>
        ) : (
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            No active version found for {forRole === 'CUSTOMER' ? 'customers' : 'sellers'}.
          </p>
        )}
      </div>

      {/* Content */}
      {loading && (
        <div className="page-loader"><div className="spinner" /></div>
      )}
      {!loading && filtered.length === 0 && (
        <div className="empty-state">
          <FileText size={48} />
          <h3>No Terms & Conditions found</h3>
          <p>{search ? 'Try a different search term' : 'Publish the first version to get started'}</p>
          {!search && (
            <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setModalState({ mode: 'create' })}>
              <Plus size={14} /> Publish Version
            </button>
          )}
        </div>
      )}
      {!loading && filtered.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Version</th>
                <th>Title</th>
                <th>Status</th>
                <th>Last Updated</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.id}>
                  <td style={{ fontWeight: 700 }}>v{t.version}</td>
                  <td>{t.title}</td>
                  <td>
                    <span className={`badge ${t.isActive ? 'active' : 'inactive'}`}>
                      {t.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 12.5 }}>{formatDate(t.updatedAt)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button
                        className="btn btn-ghost btn-icon btn-sm"
                        title="View content"
                        onClick={() => setModalState({ mode: 'view', term: t })}
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        className="btn btn-ghost btn-icon btn-sm"
                        title="Edit this version"
                        onClick={() => setModalState({ mode: 'edit', term: t })}
                      >
                        <Pencil size={14} />
                      </button>
                      {!t.isActive && (
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          title="Re-activate this version"
                          onClick={() => setModalState({ mode: 'activate', term: t })}
                        >
                          <RotateCcw size={14} />
                        </button>
                      )}
                      {!t.isActive && (
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          title="Delete this version"
                          style={{ color: 'var(--red)' }}
                          onClick={() => setModalState({ mode: 'delete', term: t })}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filtered.length > 0 && (
        <div style={{ marginTop: 16, fontSize: 13, color: 'var(--text-muted)' }}>
          Showing {filtered.length} of {terms.length} versions
        </div>
      )}

      {modalState?.mode === 'create' && (
        <CreateTermsModal
          forRole={forRole}
          existingVersions={terms.map(t => t.version)}
          onSave={handleCreate}
          onClose={() => setModalState(null)}
          loading={actionLoading}
        />
      )}
      {modalState?.mode === 'view' && (
        <ViewTermsModal term={modalState.term} onClose={() => setModalState(null)} />
      )}
      {modalState?.mode === 'edit' && (
        <EditTermsModal
          forRole={forRole}
          term={modalState.term}
          onSave={handleEdit}
          onClose={() => setModalState(null)}
          loading={actionLoading}
        />
      )}
      {modalState?.mode === 'activate' && (
        <ActivateModal
          forRole={forRole}
          term={modalState.term}
          onConfirm={handleActivate}
          onClose={() => setModalState(null)}
          loading={actionLoading}
        />
      )}
      {modalState?.mode === 'delete' && (
        <DeleteTermsModal
          forRole={forRole}
          term={modalState.term}
          onConfirm={handleDelete}
          onClose={() => setModalState(null)}
          loading={actionLoading}
        />
      )}
    </Layout>
  );
}